import {
  CreateCustomerResponseBody,
  CreateDriverResponseBody,
  CreateFinancialMonthResponseBody,
  CreateFinancialTransactionResponseBody,
  CreateInvoiceResponseBody,
  CreateOrderPrepareResponseBody,
  CreateOrderResponseBody,
  CreateOutletResponseBody,
  CreateProductResponseBody,
  CreateReturnOrderResponseBody,
  CreateStockAdjustmentResponseBody,
  CreateUserResponseBody,
  GetAppSettingResponseBody,
  GetCommissionsResponseBody,
  GetCreditsResponseBody,
  GetCustomerResponseBody,
  GetCustomersResponseBody,
  GetDriverResponseBody,
  GetDriversResponseBody,
  GetEmptiesByCustomerOrderResponseBody,
  GetEmptiesByCustomerResponseBody,
  GetEmptiesRecHistoryResponseBody,
  GetEmptiesResponseBody,
  GetFinancialMonthsResponseBody,
  GetFinancialTransactionResponseBody,
  GetInvoicesResponseBody,
  GetOrderResponseBody,
  GetOrdersResponseBody,
  GetOutletResponseBody,
  GetOutletsResponseBody,
  GetProductResponseBody,
  GetProductsResponseBody,
  GetReturnOrderResponseBody,
  GetReturnOrdersResponseBody,
  GetStockAdjustmentsResponseBody,
  GetUserResponseBody,
  GetUsersResponseBody,
  UpdateCustomerResponseBody,
  UpdateDriverResponseBody,
  UpdateOutletResponseBody,
  UpdateProductResponseBody,
  UpdateUserResponseBody
} from 'khufu-legos';

import { Customer, Driver, FinancialTransaction, Outlet, Product, StockAdjustment, User } from '../types';
import { authApi } from '../utils/api';

type OrderPrepareItemsInput = {
  productId: string;
  quantity: number;
};

type CreditInfo = {
  outletId?: string;
  amount: string;
};

type CreateOrderPrepareRequestBody = { customerId: string; items: OrderPrepareItemsInput[]; type: string };

export const getUsers = async (queryParams: Record<string, unknown>) =>
  (await authApi<GetUsersResponseBody>({ method: 'GET', url: '/users', params: queryParams })).data;
export const getProducts = async (queryParams?: Record<string, unknown>) =>
  (await authApi<GetProductsResponseBody>({ method: 'GET', url: '/product', params: queryParams })).data;
export const getCustomers = async (queryParams?: Record<string, unknown>) =>
  (await authApi<GetCustomersResponseBody>({ method: 'GET', url: '/customer/all', params: queryParams })).data;
export const getOutlets = async (queryParams: Record<string, unknown>) =>
  (await authApi<GetOutletsResponseBody>({ method: 'GET', url: '/outlet/all', params: queryParams })).data;
export const getDrivers = async (queryParams: Record<string, unknown>) =>
  (await authApi<GetDriversResponseBody>({ method: 'GET', url: '/driver/all', params: queryParams })).data;
export const getEmpties = async () => (await authApi<GetEmptiesResponseBody>({ method: 'GET', url: '/empties' })).data;
export const getEmptiesByCustomer = async () =>
  (await authApi<GetEmptiesByCustomerResponseBody>({ method: 'GET', url: '/empties/customers' })).data;
export const getEmptiesHistory = async (queryParams?: Record<string, unknown>) =>
  (await authApi<GetEmptiesRecHistoryResponseBody>({ method: 'GET', url: '/empties/history', params: queryParams }))
    .data;
export const getFinancialTransaction = async (queryParams?: Record<string, unknown>) =>
  (
    await authApi<GetFinancialTransactionResponseBody>({
      method: 'GET',
      url: '/financial-transaction/all',
      params: queryParams
    })
  ).data;
export const getInvoices = async (queryParams?: Record<string, unknown>) =>
  (await authApi<GetInvoicesResponseBody>({ method: 'GET', url: '/invoices', params: queryParams })).data;
export const getCommissions = async (queryParams?: Record<string, unknown>) =>
  (await authApi<GetCommissionsResponseBody>({ method: 'GET', url: '/commissions', params: queryParams })).data;
export const getOrders = async (queryParams?: Record<string, unknown>) =>
  (await authApi<GetOrdersResponseBody>({ method: 'GET', url: '/orders', params: queryParams })).data;
export const getReturnOrders = async (queryParams?: Record<string, unknown>) =>
  (await authApi<GetReturnOrdersResponseBody>({ method: 'GET', url: '/orders/returns', params: queryParams })).data;

export const getCredits = async (queryParams?: Record<string, unknown>) =>
  (await authApi<GetCreditsResponseBody>({ method: 'GET', url: '/credits', params: queryParams })).data;

export const getStockAdjustments = async (queryParams?: Record<string, unknown>) =>
  (await authApi<GetStockAdjustmentsResponseBody>({ method: 'GET', url: '/stock-adjustment', params: queryParams }))
    .data;

export const getUser = async (id: string) => await authApi<GetUserResponseBody>({ method: 'GET', url: `/user/${id}` });
export const getCustomer = async (id: string) =>
  await authApi<GetCustomerResponseBody>({ method: 'GET', url: `/customer/${id}` });
export const getProduct = async (id: string) =>
  await authApi<GetProductResponseBody>({ method: 'GET', url: `/product/${id}` });
export const getOutlet = async (id: string) =>
  await authApi<GetOutletResponseBody>({ method: 'GET', url: `/outlet/${id}` });
export const getDriver = async (id: string) =>
  await authApi<GetDriverResponseBody>({ method: 'GET', url: `/driver/${id}` });
export const getOrder = async (id: string) =>
  await authApi<GetOrderResponseBody>({ method: 'GET', url: `/orders/${id}` });
export const getReturnOrder = async (id: string) =>
  await authApi<GetReturnOrderResponseBody>({ method: 'GET', url: `/orders/returns/${id}` });
export const getEmptiesByCustomerOrder = async (customerId: string) =>
  (
    await authApi<GetEmptiesByCustomerOrderResponseBody>({
      method: 'POST',
      url: '/empties/customer/order',
      data: { customerId }
    })
  ).data;

export const getOutletsOwed = async (orderId: string) =>
  await authApi<any>({ method: 'GET', url: '/credits', params: { orderId } });
export const getFinancialMonth = async () =>
  (await authApi<GetFinancialMonthsResponseBody>({ method: 'GET', url: '/financial-month' })).data;
/** Commission rates as a percentage of each item's value; unset means the API's default (20% / 10%). Not yet in khufu-legos. */
export type CommissionPercentSettings = { driverCommissionPercent?: number; motorBoyCommissionPercent?: number };

export const getAdminSettings = async () =>
  (await authApi<GetAppSettingResponseBody>({ method: 'GET', url: '/app-settings' })).data as
    (GetAppSettingResponseBody['data'] & CommissionPercentSettings) | undefined;

export const registerUser = async (data: Partial<User>) =>
  await authApi<CreateUserResponseBody>({ method: 'POST', url: '/auth/register', data });
export const createCustomer = async (data: Partial<Customer>) =>
  await authApi<CreateCustomerResponseBody>({ method: 'POST', url: '/customer/create', data });
export const createProduct = async (data: Partial<Product>) =>
  await authApi<CreateProductResponseBody>({ method: 'POST', url: '/product/create', data });
export const createOutlet = async (data: Partial<Outlet>) =>
  await authApi<CreateOutletResponseBody>({ method: 'POST', url: '/outlet/create', data });
export const createDriver = async (data: Partial<Driver>) =>
  await authApi<CreateDriverResponseBody>({ method: 'POST', url: '/driver/create', data });
export const createStockAdjustment = async (data: Partial<StockAdjustment>) =>
  await authApi<CreateStockAdjustmentResponseBody>({ method: 'POST', url: '/stock-adjustment', data });
export const createOrder = async (
  orderPrepareId: string,
  driverIds: string[],
  motorboyIds: string[],
  comments?: string
) =>
  await authApi<CreateOrderResponseBody>({
    method: 'POST',
    url: '/orders/create',
    data: { orderPrepareId, driverIds, motorboyIds, comments }
  });
export const createReturn = async (
  orderId: string,
  reason: string,
  items: { orderItemId: string; returnQty: number }[]
) =>
  await authApi<CreateReturnOrderResponseBody>({
    method: 'POST',
    url: '/orders/returns',
    data: { orderId, reason, items }
  });

export const createReturnEmpties = async (
  customerId: string,
  amount: string,
  paymentMode: string,
  type: string,
  emptiesItems: { productId: string; quantity: string }[]
) =>
  await authApi<any>({
    method: 'POST',
    url: '/empties/return',
    data: { amount, paymentMode, customerId, type, emptiesItems }
  });

export const createInvoice = async (
  orderId: string,
  amount: string,
  modeOfPayment: string,
  comments?: string,
  creditInfo?: CreditInfo[]
) =>
  await authApi<CreateInvoiceResponseBody>({
    method: 'POST',
    url: '/invoices/create',
    data: { orderId, amount, modeOfPayment, comments, creditInfo }
  });

export const createFinancialTransaction = async (data: Partial<FinancialTransaction>) =>
  await authApi<CreateFinancialTransactionResponseBody>({ method: 'POST', url: '/financial-transaction/create', data });

export const createFinancialMonth = async (startDate: string, endDate: string) =>
  await authApi<CreateFinancialMonthResponseBody>({
    method: 'POST',
    url: '/financial-month/create',
    data: { startDate, endDate }
  });

export const updateCustomer = async (id: string, data: Partial<Customer>) =>
  await authApi<UpdateCustomerResponseBody>({ method: 'PUT', url: `/customer/${id}`, data });
export const updateUser = async (id: string, data: Partial<User>) =>
  await authApi<UpdateUserResponseBody>({ method: 'PUT', url: `/user/${id}`, data });
export const updateProduct = async (data: Partial<Product>) =>
  await authApi<UpdateProductResponseBody>({ method: 'PUT', url: `/product/${data._id}`, data });
export const updateOutlet = async (id: string, data: Partial<Outlet>) =>
  await authApi<UpdateOutletResponseBody>({ method: 'PUT', url: `/outlet/${id}`, data });
export const updateDriver = async (id: string, data: Partial<Driver>) =>
  await authApi<UpdateDriverResponseBody>({ method: 'PUT', url: `/driver/${id}`, data });
export const updateNotificationEmail = async (data: { emails: string[] }) =>
  await authApi<any>({
    method: 'PUT',
    url: '/app-settings/notification-emails',
    data
  });
export const updateStaffCommission = async (data: Required<CommissionPercentSettings>) =>
  await authApi<any>({
    method: 'PUT',
    url: '/app-settings/staff-commissions',
    data
  });

export const orderPrepare = async (customerId: string, data: Partial<CreateOrderPrepareRequestBody>) =>
  await authApi<CreateOrderPrepareResponseBody>({
    method: 'POST',
    url: 'orders/prepare',
    data: { ...data, customerId }
  });

export const dissmissBanner = async () =>
  await authApi({
    method: 'POST',
    url: 'user/dismiss-banner',
    data: {}
  });

export const cancelOrder = async (orderId: string, reason: string) =>
  await authApi<any>({
    method: 'POST',
    url: '/orders/cancel',
    data: { orderId, reason }
  });

export const cancelOrderWithReturns = async (orderId: string, reason: string) =>
  await authApi<any>({
    method: 'POST',
    url: '/orders/cancelOrderWithReturn',
    data: { orderId, reason }
  });
